<template>
  <CustomModal v-if="open" @close="$emit('close')" class="invest-modal">
    <template v-slot:body>
      <LeftGradientBlockLayout>
        <LeftGradientBlockLayoutHead>
          <template slot="title">{{ $t('modalTitle') }}</template>
        </LeftGradientBlockLayoutHead>
        <LeftGradientBlockLayoutBody>
          <template slot="left">
            <ValueInput v-model.trim="form_data.sum_to"
                        placeholder="0.00"
                        :label="$t('valueLabel')"
                        :currency_id="form_data.currency.id"
                        :class="{'--error':$v.form_data.sum_to.$error }">
              <template v-slot:errors="">
                <div class="error" v-if="!$v.form_data.sum_to.required">{{ $t('errors.required') }}</div>
              </template>
            </ValueInput>
          </template>
          <template slot="right">
            <BaseSelect v-model="form_data.currency"
                        :options="currenciesList"
                        label="name_ru"
                        :select-label="$t('currencyLabel')"
                        track-by="id"
                        placeholder="Введите название"
                        :internal-search="false"
                        :allow-empty="false"
                        :close-on-select="true"
                        :show-labels="false"
                        :show-no-results="false"
                        @input="form_data.currency = $event"/>
            <AccountsSelect :label="$t('accountLabel')"
                            :value="form_data.account_to"
                            :accounts="accounts.data"
                            :account_groups="accounts_by_groups"
                            :class="{'--error':$v.form_data.account_to.$error}"
                            :grouped="accounts_by_groups.length > 1"
                            @input="form_data.account_to = $event">
              <template v-slot:errors>
                <div class="error" v-if="$v.form_data.account_to.$invalid">{{ $t('errors.required') }}</div>
              </template>
            </AccountsSelect>
            <BaseButton theme="red" @click="addDeal" :loading="submitted">{{ $t('buttonLabel') }}</BaseButton>
            <div class="flex items-center">
              <VueCtkDateTimePicker id="transaction-date"
                                    class="inline"
                                    v-model="form_data.date"
                                    format="YYYY-MM-DD"
                                    :formatted="dateFormatted"
                                    :locale="$i18n.locale"
                                    :auto-close="true"
                                    :only-date="true"
                                    :no-label="true"
                                    :no-clear-button="true"
                                    :no-button="true"
                                    color="#2D4C5E"/>
              <VueCtkDateTimePicker id="transaction-time"
                                    class="inline"
                                    v-model="form_data.time"
                                    format="HH:mm"
                                    formatted="HH:mm"
                                    :locale="$i18n.locale"
                                    :only-time="true"
                                    :no-clear-button="true"
                                    :no-label="true"
                                    :no-button="true"
                                    color="#2D4C5E"/>
            </div>
          </template>
        </LeftGradientBlockLayoutBody>
      </LeftGradientBlockLayout>
    </template>
  </CustomModal>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import {decimal, required} from "vuelidate/lib/validators";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import CustomModal from "@/components/UI/modals/CustomModal";
import BaseButton from "@/components/UI/BaseButton";
import ValueInput from "@/components/UI/BaseInput/ValueInput";
import AccountsSelect from "@/components/UI/BaseSelect/AccountsSelect";
import LeftGradientBlockLayout from "@/components/layouts/cabinet/LeftGradientBlockLayout/LeftGradientBlockLayout";
import LeftGradientBlockLayoutHead
  from "@/components/layouts/cabinet/LeftGradientBlockLayout/LeftGradientBlockLayoutHead";
import LeftGradientBlockLayoutBody
  from "@/components/layouts/cabinet/LeftGradientBlockLayout/LeftGradientBlockLayoutBody";

export default {
  name: 'TransferOutBalanceModal',
  components: {
    LeftGradientBlockLayoutBody,
    LeftGradientBlockLayoutHead,
    LeftGradientBlockLayout,
    AccountsSelect,
    ValueInput,
    BaseButton,
    CustomModal,
    BaseSelect,
  },
  props: {
    open: {
      required: true
    },
  },
  data() {
    return {
      submitted: false,
      form_data: {
        account_to: null,
        sum_to: null,
        currency: {
          id: 123,
          code: "RUB",
          symbol: "₽",
          name_en: "Russian Ruble",
          name_ru: "Российский рубль",
          is_base: 1,
          is_relative: 1,
          decimals: 2,
          is_active: 1
        },
        date: null,
        time: null,
      },
      currenciesList: [
        {
          id: 123,
          code: "RUB",
          symbol: "₽",
          name_en: "Russian Ruble",
          name_ru: "Российский рубль",
          is_base: 1,
          is_relative: 1,
          decimals: 2,
          is_active: 1
        },
        {
          id: 150,
          code: "USD",
          symbol: "$",
          name_en: "United States Dollar",
          name_ru: "Доллар США",
          is_base: 1,
          is_relative: 1,
          decimals: 2,
          is_active: 1
        },
        {
          id: 47,
          code: "EUR",
          symbol: "€",
          name_en: "Euro",
          name_ru: "Евро",
          is_base: null,
          is_relative: null,
          decimals: 2,
          is_active: 1
        }],
    }
  },
  computed: {
    ...mapState('space', ['current_space']),
    ...mapState('space/accounts', ['accounts']),
    ...mapGetters('space/accounts', ['accounts_by_groups']),
    space_id() {
      return this.$store.state.space.current_space.data.id
    },
    portfolio_id() {
      return this.$store.state.investments.portfolios.current_portfolio.data.id
    },
    dateFormatted() {
      const isToday = this.$moment(this.form_data.date, 'YYYY-MM-DD').startOf('day').unix() === this.$moment().startOf('day').unix()

      return isToday ? this.$t('today') : 'DD.MM.YYYY'
    }
  },
  methods: {
    ...mapActions('investments/portfolios', ['updatePortfolioBalance']),
    resetFields() {
      this.submitted = false;
      this.form_data.account_to = null
      this.form_data.sum_to = null
      this.form_data.currency = {
        id: 123,
        code: "RUB",
        symbol: "₽",
        name_en: "Russian Ruble",
        name_ru: "Российский рубль",
        is_base: 1,
        is_relative: 1,
        decimals: 2,
        is_active: 1
      }
      this.setInitialDate()
      this.$v.$reset();
    },
    addDeal() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        const data = {
          space_id: this.space_id,
          to_id: this.form_data.account_to.id,
          sum_to: this.form_data.sum_to,
          portfolio_id: this.portfolio_id,
          currency: this.form_data.currency.code,
          date_time: `${this.form_data.date} ${this.form_data.time}:00`,
        }
        this.updatePortfolioBalance(data).then((res) => {
          if (res.body.success) {
            this.$store.dispatch('investments/portfolios/getPortfolios').then(() => {
              this.$store.dispatch('investments/portfolios/updateCurrentPortfolioInfo')
            })
            this.$store.dispatch('investments/deals/fetchDealsHistory')
            this.resetFields();
            this.$emit('close')
          }
        })
      }
    },
    setInitialDate() {
      this.form_data.date = this.$moment().format('YYYY-MM-DD');
      this.form_data.time = this.$moment().format('HH:mm');
    }
  },
  watch: {
    open(newVal) {
      if (newVal) {
        this.resetFields();
      }
    }
  },
  mounted() {
    this.setInitialDate()
  },
  validations() {
    return {
      form_data: {
        account_to: {
          required,
        },
        sum_to: {
          required,
          decimal,
          minValue: 0.01,
        }
      },
    }
  },
  i18n: {
    messages: {
      en: {
        modalTitle: 'Withdraw funds',
        valueLabel: 'Amount of withdrawal',
        currencyLabel: 'Debit Currency',
        accountLabel: 'Account to be credited',
        buttonLabel: 'Display',
        today: '[Today]'
      },
      ru: {
        modalTitle: 'Вывод средств',
        valueLabel: 'Сумма вывода',
        currencyLabel: 'Валюта списания',
        accountLabel: 'Счет для начисления',
        buttonLabel: 'Вывести',
        today: 'Сегодня'
      }
    }
  }
}
</script>